import { useState, useEffect } from "react";
import { useUserJourney } from "./useUserJourney";
import { trackRatingDialogShown } from "@/lib/mixpanelRatingEvents";

const DAYS_UNTIL_PROMPT = 7;
const RATE_APP_SHOWN_KEY = "rateAppShown";
const RATE_APP_REMIND_AT_KEY = "rateAppRemindAt";
const RATE_APP_COMPLETED_KEY = "rateAppCompleted";

export const useAppRating = () => {
  const [showRateDialog, setShowRateDialog] = useState(false);
  const { data: userJourney, isLoading, isError } = useUserJourney();

  useEffect(() => {
    // Don't run if still loading or there was an error
    if (isLoading || isError || !userJourney) {
      return;
    }

    const checkShouldShowRating = () => {
      // Never show if user has already completed rating
      const hasCompleted = localStorage.getItem(RATE_APP_COMPLETED_KEY);
      if (hasCompleted) {
        return false;
      }

      const hasShown = localStorage.getItem(RATE_APP_SHOWN_KEY);
      const remindAt = localStorage.getItem(RATE_APP_REMIND_AT_KEY);

      // If there's a reminder set, check if it's time
      if (remindAt) {
        const remindDate = new Date(remindAt);
        if (new Date() < remindDate) {
          return false; // Not time yet
        }
        // Time has passed - clear reminder and show dialog
        localStorage.removeItem(RATE_APP_REMIND_AT_KEY);
      } else if (hasShown) {
        // Already shown and no active reminder - don't show again
        return false;
      }

      // Check if user has been using the app for 7 days
      if (!userJourney?.created_at) {
        return false;
      }

      const createdAt = new Date(userJourney.created_at);
      const daysSinceCreation = Math.floor(
        (new Date().getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (daysSinceCreation >= DAYS_UNTIL_PROMPT) {
        setShowRateDialog(true);
        localStorage.setItem(RATE_APP_SHOWN_KEY, "true");
        trackRatingDialogShown(daysSinceCreation);
        return true;
      }

      return false;
    };

    // Small delay to ensure user journey is loaded
    const timeout = setTimeout(() => {
      checkShouldShowRating();
    }, 2000);

    return () => clearTimeout(timeout);
  }, [userJourney, isLoading, isError]);

  const closeRateDialog = () => {
    setShowRateDialog(false);
  };

  return {
    showRateDialog,
    closeRateDialog,
  };
};
